/*
 * AdminOverrides — global audit log of every admin override action.
 *
 * Sections:
 *   1. KPI strip — total / applied / pending / reverted / total $ impact
 *   2. Filter bar — action / target_type / target_user / actor / status
 *   3. Override table with expandable diff (before → after) per row
 *   4. Pending-approval block (4-eyes second admin approves)
 *   5. Revert button per applied row (creates compensating audit row)
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Shield, Search, RotateCcw, CheckCircle2, AlertTriangle, Loader2,
  ChevronDown, ChevronRight, User, Eye, Activity, TrendingUp,
  Moon, Clock, Repeat, Crosshair, Link2, ShieldCheck, Copy, Hash, Lock,
} from "lucide-react";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { PageHeader } from "../../components/PortalShell";
import { toast } from "sonner";

const money = (n) =>
  `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;


const STATUS_TONE = {
  applied:           "bg-emerald-100 text-emerald-700 border-emerald-200",
  reverted:          "bg-rose-100 text-rose-700 border-rose-200",
  pending_approval:  "bg-amber-100 text-amber-800 border-amber-200",
};

const ACTION_GROUPS = {
  "account":   ["suspend_user", "unsuspend_user", "reset_password", "reset_2fa", "kick_sessions", "edit_profile", "force_verify_email", "force_re_kyc", "impersonate"],
  "money":     ["wallet_credit", "wallet_debit", "force_paid_invoice", "void_invoice", "force_refund_invoice", "void_revenue_row", "cancel_sweep", "force_payout", "set_credit_limit", "score_override"],
  "order":     ["force_cancel_order", "force_order_status", "edit_order", "waive_sla"],
  "credit":    ["credit_app_approved", "credit_app_denied", "grant_allianz_cover", "revoke_allianz_cover"],
  "claims":    ["force_settled_claim", "force_denied_claim", "force_auto_closed_claim", "force_dropship_claim_approved", "force_dropship_claim_rejected", "force_dropship_return_verified", "force_dropship_return_rejected"],
  "tenant":    ["edit_tenant", "pause_storefront", "unpause_storefront", "disconnect_connection", "app_install", "app_uninstall"],
  "support":   ["force_close_ticket"],
};


export default function AdminOverrides() {
  const [data, setData]   = useState({ rows: [], count: 0, summary: {} });
  const [loading, setLoading] = useState(true);
  const [fAction, setFAction] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fTargetType, setFTargetType] = useState("");
  const [fSearch, setFSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [revertOpen, setRevertOpen] = useState(null);
  const [revertReason, setRevertReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [anomalies, setAnomalies] = useState(null);
  const [anomLoading, setAnomLoading] = useState(false);
  const [anomOpen, setAnomOpen] = useState(false);
  const [anomFocus, setAnomFocus] = useState(null);

  const refreshAnomalies = async () => {
    setAnomLoading(true);
    try {
      const r = await api.get("/admin/overrides/anomalies");
      setAnomalies(r.data);
    } catch {
      // surface silently — anomaly panel is non-blocking
    } finally { setAnomLoading(false); }
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fAction) params.set("action", fAction);
      if (fStatus) params.set("status", fStatus);
      if (fTargetType) params.set("target_type", fTargetType);
      params.set("limit", "200");
      const r = await api.get(`/admin/overrides?${params}`);
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load overrides");
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [fAction, fStatus, fTargetType]);
  useEffect(() => { refreshAnomalies(); }, []);

  const filtered = useMemo(() => {
    if (!fSearch) return data.rows;
    const q = fSearch.trim().toLowerCase();
    return data.rows.filter(r =>
      (r.actor_name || "").toLowerCase().includes(q) ||
      (r.target_user_name || "").toLowerCase().includes(q) ||
      (r.reason || "").toLowerCase().includes(q) ||
      (r.action || "").toLowerCase().includes(q) ||
      (r.target_id || "").toLowerCase().includes(q)
    );
  }, [data.rows, fSearch]);

  const revert = async () => {
    if (!revertOpen) return;
    if (!revertReason || revertReason.trim().length < 3) {
      toast.error("Reason required (min 3 chars)");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/overrides/${revertOpen.id}/revert`,
                     { reason: revertReason.trim() });
      toast.success("Override reverted · compensating audit row created");
      setRevertOpen(null); setRevertReason("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revert failed");
    } finally { setBusy(false); }
  };

  const approve = async (id) => {
    try {
      await api.post(`/admin/overrides/${id}/approve`);
      toast.success("Approved · override applied");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approval failed");
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="admin-overrides">
      <PageHeader
        title="Admin Override Audit"
        subtitle="Every admin action across buyer + seller portals · reason · diff · revert · hash-chained · SOC 2 CC7.2"
      />

      {/* Hash-chain integrity */}
      <ChainIntegrityCard/>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Total overrides" value={data.summary.total ?? 0} testid="ovr-kpi-total"/>
        <Kpi label="Applied" value={data.summary.applied ?? 0} tone="good" testid="ovr-kpi-applied"/>
        <Kpi label="Pending approval" value={data.summary.pending ?? 0} tone={data.summary.pending > 0 ? "warn" : "neutral"} testid="ovr-kpi-pending"/>
        <Kpi label="Reverted" value={data.summary.reverted ?? 0} tone={data.summary.reverted > 0 ? "warn" : "neutral"} testid="ovr-kpi-reverted"/>
        <Kpi label="Total $ impact" value={money(data.summary.impact ?? 0)} tone="ink" testid="ovr-kpi-impact"/>
      </div>

      {/* Anomaly detection panel */}
      <AnomalyPanel data={anomalies} loading={anomLoading}
        onRefresh={refreshAnomalies}
        onOpenDetail={() => setAnomOpen(true)}
        onFilterByActor={(actor_id) => {
          setFSearch(actor_id || "");
        }}
        onJumpToRule={(rule) => { setAnomFocus(rule); setAnomOpen(true); }}/>

      {/* Filter bar */}
      <div className="rounded-2xl border bg-background p-3 flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-[10px] uppercase tracking-wider text-foreground/55">Search</Label>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40"/>
            <Input className="pl-8 h-9" placeholder="actor / target / reason / action / id"
              value={fSearch} onChange={(e) => setFSearch(e.target.value)}
              data-testid="ovr-search"/>
          </div>
        </div>
        <div className="w-[170px]">
          <Label className="text-[10px] uppercase tracking-wider text-foreground/55">Action</Label>
          <select className="w-full h-9 border border-input rounded-md px-2 text-sm bg-background"
            value={fAction} onChange={(e) => setFAction(e.target.value)} data-testid="ovr-filter-action">
            <option value="">All actions</option>
            {Object.entries(ACTION_GROUPS).map(([group, list]) => (
              <optgroup key={group} label={group}>
                {list.map(a => <option key={a} value={a}>{a}</option>)}
              </optgroup>
            ))}
          </select>
        </div>
        <div className="w-[150px]">
          <Label className="text-[10px] uppercase tracking-wider text-foreground/55">Target type</Label>
          <select className="w-full h-9 border border-input rounded-md px-2 text-sm bg-background"
            value={fTargetType} onChange={(e) => setFTargetType(e.target.value)} data-testid="ovr-filter-target">
            <option value="">All targets</option>
            {["user", "invoice", "order", "wallet", "claim", "tenant",
              "connection", "credit_app", "allianz_cover", "platform_revenue",
              "treasury_sweep", "support_ticket", "dropship_claim",
              "dropship_return", "app"].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="w-[140px]">
          <Label className="text-[10px] uppercase tracking-wider text-foreground/55">Status</Label>
          <select className="w-full h-9 border border-input rounded-md px-2 text-sm bg-background"
            value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="ovr-filter-status">
            <option value="">All</option>
            <option value="applied">Applied</option>
            <option value="pending_approval">Pending</option>
            <option value="reverted">Reverted</option>
          </select>
        </div>
        <Button size="sm" variant="ghost" onClick={() => { setFAction(""); setFStatus(""); setFTargetType(""); setFSearch(""); }}>
          Clear
        </Button>
      </div>

      {/* Pending block */}
      {filtered.some(r => r.status === "pending_approval") && (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="ovr-pending-block">
          <div className="flex items-center gap-2 mb-2 text-amber-900">
            <AlertTriangle className="w-4 h-4"/>
            <span className="font-semibold text-sm">Pending second-admin approval</span>
          </div>
          <div className="space-y-2">
            {filtered.filter(r => r.status === "pending_approval").map(r => (
              <div key={r.id} className="flex items-center justify-between text-xs bg-white border rounded-lg px-3 py-2">
                <div>
                  <div className="font-medium">{r.action} · {money(r.impact_usd)}</div>
                  <div className="text-[10.5px] text-foreground/65">
                    Requested by {r.actor_name} · target {r.target_user_name || r.target_id}
                  </div>
                </div>
                <Button size="sm" variant="default" onClick={() => approve(r.id)} data-testid={`ovr-approve-${r.id}`}>
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1"/>Approve
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-5 h-5 animate-spin text-foreground/40"/>
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border bg-background p-8 text-center text-foreground/55 text-sm">
          No overrides match these filters.
        </div>
      ) : (
        <div className="rounded-2xl border bg-background overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-foreground/55 border-b">
                <th className="px-3 py-2 font-medium w-6"></th>
                <th className="px-3 py-2 font-medium">When</th>
                <th className="px-3 py-2 font-medium">Action</th>
                <th className="px-3 py-2 font-medium">Target</th>
                <th className="px-3 py-2 font-medium">Actor</th>
                <th className="px-3 py-2 font-medium">Reason</th>
                <th className="px-3 py-2 font-medium text-right">$ Impact</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Hash</th>
                <th className="px-3 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const expanded = expandedId === r.id;
                return (
                  <React.Fragment key={r.id}>
                    <tr className="border-b last:border-0 hover:bg-foreground/3" data-testid={`ovr-row-${r.id}`}>
                      <td className="px-3 py-2 align-top">
                        <button onClick={() => setExpandedId(expanded ? null : r.id)} data-testid={`ovr-expand-${r.id}`}>
                          {expanded ? <ChevronDown className="w-3.5 h-3.5"/> : <ChevronRight className="w-3.5 h-3.5"/>}
                        </button>
                      </td>
                      <td className="px-3 py-2 align-top text-[11px] text-foreground/65 whitespace-nowrap">
                        {new Date(r.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                      </td>
                      <td className="px-3 py-2 align-top text-[12px] font-mono">{r.action}</td>
                      <td className="px-3 py-2 align-top text-[11px]">
                        <div>{r.target_user_name || <span className="text-foreground/40">—</span>}</div>
                        <div className="text-[10px] text-foreground/55">{r.target_type} · {(r.target_id || "").slice(0, 12)}</div>
                      </td>
                      <td className="px-3 py-2 align-top text-[11px]">{r.actor_name}</td>
                      <td className="px-3 py-2 align-top text-[11px] max-w-xs truncate" title={r.reason}>{r.reason}</td>
                      <td className="px-3 py-2 align-top text-right tabular-nums text-[11px] font-medium">
                        {r.impact_usd ? money(r.impact_usd) : "—"}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10.5px] border ${STATUS_TONE[r.status] || "bg-foreground/5"}`}>
                          {(r.status || "applied").replace("_", " ")}
                        </span>
                        {r.is_revert && (
                          <div className="text-[9.5px] text-rose-700 mt-0.5">revert action</div>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top">
                        {r.event_hash ? (
                          <div className="flex items-center gap-1 group" title={`event: ${r.event_hash}\nprev: ${r.prev_hash || ""}\npos: ${r.chain_position}`}>
                            <Lock className="w-3 h-3 text-emerald-600 flex-shrink-0"/>
                            <span className="font-mono text-[10px] text-foreground/70" data-testid={`ovr-hash-${r.id}`}>
                              #{r.chain_position} · {r.event_hash.slice(0, 8)}
                            </span>
                          </div>
                        ) : (
                          <span className="text-[10px] text-foreground/40 font-mono">unchained</span>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top text-right">
                        {r.status === "applied" && !r.is_revert && (
                          <button onClick={() => { setRevertOpen(r); setRevertReason(""); }} className="text-rose-700 hover:underline text-[11px]" data-testid={`ovr-revert-${r.id}`}>
                            <RotateCcw className="w-3 h-3 inline mr-0.5"/>Revert
                          </button>
                        )}
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="border-b last:border-0 bg-foreground/[0.02]">
                        <td/>
                        <td colSpan={9} className="px-3 py-3 text-[11px]">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <div className="text-[9.5px] uppercase tracking-wider text-foreground/55 mb-1">Before</div>
                              <pre className="bg-rose-50 border border-rose-100 rounded p-2 text-[10px] overflow-x-auto whitespace-pre-wrap">{JSON.stringify(r.before || {}, null, 2)}</pre>
                            </div>
                            <div>
                              <div className="text-[9.5px] uppercase tracking-wider text-foreground/55 mb-1">After</div>
                              <pre className="bg-emerald-50 border border-emerald-100 rounded p-2 text-[10px] overflow-x-auto whitespace-pre-wrap">{JSON.stringify(r.after || {}, null, 2)}</pre>
                            </div>
                          </div>
                          {r.event_hash && (
                            <div className="mt-3 rounded-lg border bg-emerald-50/40 border-emerald-200 p-2 space-y-1">
                              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-emerald-800">
                                <Lock className="w-3 h-3"/>Hash-chain integrity
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[10.5px] font-mono">
                                <div><span className="text-foreground/55">Position:</span> #{r.chain_position}</div>
                                <div className="truncate"><span className="text-foreground/55">Prev:</span> {(r.prev_hash || "").slice(0, 16)}…</div>
                                <div className="truncate"><span className="text-foreground/55">Event:</span> {r.event_hash.slice(0, 16)}…</div>
                              </div>
                            </div>
                          )}
                          <div className="mt-2 grid grid-cols-3 gap-3 text-[10.5px]">
                            <div><span className="text-foreground/55">Override ID:</span> <span className="mono">{r.id}</span></div>
                            <div><span className="text-foreground/55">Actor:</span> <span>{r.actor_email}</span></div>
                            {r.is_revert && r.metadata?.revert_of && <div><span className="text-foreground/55">Reverts:</span> <span className="mono">{r.metadata.revert_of.slice(0, 8)}</span></div>}
                            {r.approved_by && <div><span className="text-foreground/55">Approved by:</span> <span>{r.approved_by}</span></div>}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Revert dialog */}
      <Dialog open={!!revertOpen} onOpenChange={(o) => !o && setRevertOpen(null)}>
        <DialogContent className="max-w-md" data-testid="ovr-revert-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-700">Revert this override</DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-2">
            <p>You are about to compensate the following override:</p>
            {revertOpen && (
              <div className="text-xs bg-foreground/5 rounded p-2 mono">
                {revertOpen.action} · {revertOpen.target_user_name || revertOpen.target_id} · {money(revertOpen.impact_usd)}
              </div>
            )}
            <Label htmlFor="rrz">Revert reason <span className="text-rose-600">*</span></Label>
            <Textarea id="rrz" rows={3}
              placeholder="Why are you reverting?"
              value={revertReason}
              onChange={(e) => setRevertReason(e.target.value)}
              data-testid="ovr-revert-reason"/>
            <p className="text-[10.5px] text-foreground/55">
              A new compensating override row will be inserted with `is_revert=true`. The original row's
              status flips to `reverted`. Both rows remain in the audit log forever.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRevertOpen(null)}>Cancel</Button>
            <Button variant="destructive" onClick={revert} disabled={busy} data-testid="ovr-revert-confirm">
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin"/>}
              <RotateCcw className="w-4 h-4 mr-1"/>Revert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Anomaly detail drawer */}
      <AnomalyDetailDialog open={anomOpen} onClose={() => { setAnomOpen(false); setAnomFocus(null); }}
        data={anomalies} focus={anomFocus}/>
    </div>
  );
}


/* ─── Hash-chain integrity card ───────────────────────────────────── */

function ChainIntegrityCard() {
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [lastVerify, setLastVerify] = useState(null);

  const loadStatus = async () => {
    setStatusLoading(true);
    try {
      const r = await api.get("/admin/audit-chain/status");
      setStatus(r.data);
    } catch (e) {
      toast.error("Failed to load chain status");
    } finally { setStatusLoading(false); }
  };

  const verifyNow = async () => {
    setVerifying(true);
    try {
      const r = await api.get("/admin/audit-chain/verify");
      setLastVerify(r.data);
      if (r.data.valid) {
        toast.success(`Chain verified · ${r.data.verified_rows} rows in ${r.data.duration_ms}ms`);
      } else {
        toast.error(`TAMPER DETECTED at position #${r.data.broken_at}`);
      }
      loadStatus();
    } catch (e) {
      toast.error("Verification failed");
    } finally { setVerifying(false); }
  };

  const repairNow = async () => {
    if (!window.confirm("Re-stamp the chain forward from position 1?\n\nThis is safe: idempotent + audit-logged. Used to recover from manual mutations (incident response · SOC 2 CC7.3).")) return;
    setVerifying(true);
    try {
      const r = await api.post("/admin/audit-chain/repair?from_position=1");
      toast.success(`Chain repaired · ${r.data.repaired} rows re-stamped · head #${r.data.head_position}`);
      setLastVerify(null);
      loadStatus();
    } catch (e) {
      toast.error("Repair failed");
    } finally { setVerifying(false); }
  };

  const copyHash = (h) => {
    if (!h) return;
    navigator.clipboard?.writeText(h);
    toast.success("Hash copied to clipboard");
  };

  useEffect(() => { loadStatus(); }, []);

  if (statusLoading && !status) {
    return (
      <div className="rounded-2xl border bg-background p-4 flex items-center gap-2 text-sm text-foreground/55" data-testid="chain-integrity-card">
        <Loader2 className="w-4 h-4 animate-spin"/>Loading chain integrity…
      </div>
    );
  }
  if (!status) return null;

  const isHealthy = (status.last_verified_ok === true) || (status.last_verified_ok === null && status.unstamped_rows === 0);
  const isBroken  = status.last_verified_ok === false || (lastVerify && lastVerify.valid === false);
  const tone = isBroken
    ? "bg-rose-50 border-rose-300"
    : isHealthy
    ? "bg-emerald-50/40 border-emerald-200"
    : "bg-amber-50/40 border-amber-200";
  const lastVerifiedAgo = status.last_verified_at
    ? `${Math.round((Date.now() - new Date(status.last_verified_at).getTime()) / 60000)}m ago`
    : "never";
  const rowsSinceLast = status.head_position - (status.last_verified_position || 0);

  return (
    <div className={`rounded-2xl border ${tone} p-4 space-y-3`} data-testid="chain-integrity-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {isBroken
            ? <AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5 flex-shrink-0"/>
            : <ShieldCheck className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isHealthy ? "text-emerald-600" : "text-amber-600"}`}/>}
          <div>
            <div className="text-[14px] font-semibold flex items-center gap-2">
              Hash-chain integrity
              <span className={`inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border ${
                isBroken ? "bg-rose-100 border-rose-300 text-rose-800"
                : isHealthy ? "bg-emerald-100 border-emerald-200 text-emerald-700"
                : "bg-amber-100 border-amber-200 text-amber-800"
              }`}>
                <Lock className="w-2.5 h-2.5"/>
                {isBroken ? "TAMPER DETECTED" : isHealthy ? "VERIFIED" : "UNVERIFIED"}
              </span>
            </div>
            <div className="text-[11px] text-foreground/65 mt-0.5">
              SOC 2 CC7.2 · {status.stamped_rows.toLocaleString()} stamped rows · last verified {lastVerifiedAgo}
              {rowsSinceLast > 0 && <span> · <span className="font-mono">{rowsSinceLast}</span> rows since</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isBroken && (
            <Button size="sm" variant="outline" onClick={repairNow} disabled={verifying} data-testid="chain-repair-btn">
              <RotateCcw className="w-3.5 h-3.5 mr-1"/>Repair chain
            </Button>
          )}
          <Button size="sm" variant={isBroken ? "destructive" : "default"} onClick={verifyNow} disabled={verifying} data-testid="chain-verify-btn">
            {verifying ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin"/> : <ShieldCheck className="w-3.5 h-3.5 mr-1"/>}
            Verify chain now
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-[11px]">
        <div className="rounded-lg bg-background/70 border px-3 py-2">
          <div className="text-[9.5px] uppercase tracking-wider text-foreground/55 flex items-center gap-1">
            <Hash className="w-2.5 h-2.5"/>Head position
          </div>
          <div className="font-mono font-semibold tabular-nums mt-0.5" data-testid="chain-head-position">
            #{status.head_position.toLocaleString()}
          </div>
        </div>
        <div className="rounded-lg bg-background/70 border px-3 py-2 md:col-span-2">
          <div className="text-[9.5px] uppercase tracking-wider text-foreground/55 flex items-center justify-between">
            <span>Head hash</span>
            <button onClick={() => copyHash(status.head_hash)} className="hover:text-foreground" data-testid="chain-copy-head">
              <Copy className="w-2.5 h-2.5"/>
            </button>
          </div>
          <div className="font-mono mt-0.5 truncate" title={status.head_hash} data-testid="chain-head-hash">
            {(status.head_hash || "").slice(0, 24)}…{(status.head_hash || "").slice(-8)}
          </div>
        </div>
        <div className="rounded-lg bg-background/70 border px-3 py-2">
          <div className="text-[9.5px] uppercase tracking-wider text-foreground/55">Unstamped</div>
          <div className={`font-mono font-semibold tabular-nums mt-0.5 ${status.unstamped_rows > 0 ? "text-amber-700" : ""}`} data-testid="chain-unstamped">
            {status.unstamped_rows}
          </div>
        </div>
      </div>

      {lastVerify && lastVerify.valid === false && (
        <div className="rounded-lg border border-rose-300 bg-rose-100 px-3 py-2 text-[12px] text-rose-900 flex items-start gap-2" data-testid="chain-tamper-alert">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5"/>
          <div>
            <div className="font-semibold">Chain broken at position #{lastVerify.broken_at}</div>
            <div className="text-[10.5px] opacity-80 mt-0.5">
              A row's hash no longer matches its stored payload. An `admin_chain_tampers` record has been opened.
              Investigate this row and any subsequent overrides before trusting them.
            </div>
          </div>
        </div>
      )}

      {lastVerify && lastVerify.valid === true && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-100/50 px-3 py-2 text-[11px] text-emerald-900 flex items-center gap-2" data-testid="chain-verify-result">
          <CheckCircle2 className="w-3.5 h-3.5"/>
          Verified {lastVerify.verified_rows.toLocaleString()} rows in {lastVerify.duration_ms}ms · chain intact end-to-end
        </div>
      )}
    </div>
  );
}


function Kpi({ label, value, hint, tone, testid }) {
  const toneCls = tone === "good"
    ? "bg-emerald-50 border-emerald-200 text-emerald-900"
    : tone === "warn"
    ? "bg-amber-50 border-amber-200 text-amber-900"
    : tone === "ink"
    ? "bg-foreground text-background border-foreground"
    : "bg-background";
  return (
    <div className={`rounded-2xl border p-3 ${toneCls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider opacity-70 flex items-center gap-1.5">
        <Shield className="w-3 h-3"/><span>{label}</span>
      </div>
      <div className="mt-1 text-xl font-semibold tabular-nums">{value}</div>
      {hint && <div className="text-[10px] opacity-65 mt-0.5">{hint}</div>}
    </div>
  );
}



/* ─── Anomaly detection panel ─────────────────────────────────────────── */

const ANOMALY_RULES = [
  { key: "velocity_spike",    label: "Velocity spike",      icon: Activity,   tone: "rose",
    short: (t) => `>${t.velocity_n} acts in ${t.velocity_window_h}h` },
  { key: "impact_spike",      label: "Impact spike",        icon: TrendingUp, tone: "rose",
    short: (t) => `>$${(t.impact_usd / 1000).toFixed(0)}K in ${t.impact_window_h}h` },
  { key: "same_target_spam",  label: "Same-target spam",    icon: Crosshair,  tone: "amber",
    short: (t) => `>${t.same_target_n} acts on same user/${t.velocity_window_h}h` },
  { key: "revert_spike",      label: "Revert spike",        icon: Repeat,     tone: "amber",
    short: (t) => `>${t.revert_n} reverts in ${t.velocity_window_h}h` },
  { key: "off_hours_batch",   label: "Off-hours batch",     icon: Moon,       tone: "indigo",
    short: (t) => `>${t.off_hours_n} acts · ${t.off_hours_window}` },
  { key: "pending_stale",     label: "Stale pending",       icon: Clock,      tone: "amber",
    short: (t) => `Pending >${t.pending_stale_h}h` },
  { key: "high_revert_ratio", label: "High revert ratio",   icon: RotateCcw,  tone: "rose",
    short: (t) => `>${t.revert_ratio_pct}% over last ${t.revert_ratio_window}` },
];

function AnomalyPanel({ data, loading, onRefresh, onOpenDetail, onJumpToRule }) {
  const total = data?.summary?.anomalies_total ?? 0;
  const thr = data?.summary?.thresholds || {};
  return (
    <div className="rounded-2xl border bg-background overflow-hidden" data-testid="anomaly-panel">
      <div className="flex items-center justify-between px-4 py-2.5 border-b bg-foreground/3">
        <div className="flex items-center gap-2">
          <AlertTriangle className={`w-4 h-4 ${total > 0 ? "text-rose-600" : "text-foreground/45"}`}/>
          <div>
            <div className="text-[13px] font-semibold flex items-center gap-2">
              Anomaly detection
              <span className={`inline-flex items-center text-[10.5px] mono px-1.5 py-0.5 rounded border ${
                total > 0 ? "bg-rose-100 border-rose-200 text-rose-700" : "bg-foreground/5 border-border text-foreground/55"
              }`}>
                {total} signal{total === 1 ? "" : "s"}
              </span>
            </div>
            <div className="text-[10.5px] text-foreground/55">
              {data?.summary?.scanned_at ? `Scanned ${new Date(data.summary.scanned_at).toLocaleTimeString()} · 7 rules` : "—"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {total > 0 && (
            <Button size="sm" variant="outline" onClick={onOpenDetail} data-testid="anomaly-open">
              <Eye className="w-3 h-3 mr-1"/>Investigate
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={onRefresh} disabled={loading} data-testid="anomaly-refresh">
            {loading ? <Loader2 className="w-3 h-3 animate-spin"/> : "Re-scan"}
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 p-3">
        {ANOMALY_RULES.map(rule => {
          const Icon = rule.icon;
          const rows = data?.[rule.key] || [];
          const hit = rows.length;
          const tone = hit > 0
            ? (rule.tone === "rose" ? "bg-rose-50 border-rose-200 text-rose-900"
                : rule.tone === "amber" ? "bg-amber-50 border-amber-200 text-amber-900"
                : "bg-indigo-50 border-indigo-200 text-indigo-900")
            : "bg-foreground/3 border-border text-foreground/55";
          return (
            <button key={rule.key}
              onClick={() => hit > 0 && onJumpToRule(rule.key)}
              disabled={hit === 0}
              data-testid={`anomaly-tile-${rule.key}`}
              className={`rounded-lg border px-2.5 py-2 text-left transition-colors ${tone} ${hit > 0 ? "hover:opacity-80 cursor-pointer" : "cursor-default"}`}>
              <div className="flex items-center gap-1.5">
                <Icon className="w-3 h-3"/>
                <span className="text-[10.5px] font-medium leading-tight">{rule.label}</span>
              </div>
              <div className="mt-1 text-lg font-semibold tabular-nums">{hit}</div>
              <div className="text-[9.5px] opacity-70 mono leading-tight mt-0.5">{rule.short(thr)}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}


function AnomalyDetailDialog({ open, onClose, data, focus }) {
  if (!open || !data) return null;
  const rules = focus
    ? ANOMALY_RULES.filter(r => r.key === focus)
    : ANOMALY_RULES.filter(r => (data[r.key] || []).length > 0);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="anomaly-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-600"/>
            Override anomalies · {focus ? rules[0]?.label : "All signals"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          {rules.length === 0 && (
            <div className="text-center py-8 text-foreground/55 text-sm">
              <CheckCircle2 className="w-6 h-6 mx-auto mb-2 text-emerald-500"/>
              No anomalies detected. Audit feed looks clean.
            </div>
          )}
          {rules.map(rule => {
            const Icon = rule.icon;
            const rows = data[rule.key] || [];
            if (rows.length === 0) return null;
            return (
              <div key={rule.key} data-testid={`anomaly-section-${rule.key}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="w-3.5 h-3.5 text-foreground/65"/>
                  <h4 className="text-sm font-semibold">{rule.label}</h4>
                  <span className="text-[10.5px] mono text-foreground/55">{rows.length} row{rows.length === 1 ? "" : "s"}</span>
                </div>
                <div className="space-y-1.5">
                  {rows.map((row, i) => <AnomalyRow key={i} rule={rule.key} row={row}/>)}
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}


function AnomalyRow({ rule, row }) {
  if (rule === "pending_stale") {
    return (
      <div className="rounded-lg border bg-amber-50/40 px-3 py-2 text-xs">
        <div className="flex items-center justify-between">
          <div className="font-medium">{row.action} · {row.target_type}/{(row.target_id || "").slice(0, 8)}</div>
          <span className="mono text-foreground/55">{(new Date(row.created_at)).toLocaleString()}</span>
        </div>
        <div className="text-[11px] text-foreground/70 mt-0.5">
          actor: {row.actor_email} · impact: {money(row.impact_usd)}
        </div>
        <div className="text-[10.5px] text-foreground/55 mt-0.5 italic">"{row.reason}"</div>
      </div>
    );
  }
  if (rule === "high_revert_ratio") {
    return (
      <div className="rounded-lg border bg-rose-50/40 px-3 py-2 text-xs">
        <div className="flex items-center justify-between">
          <div className="font-medium">{row.actor_email}</div>
          <span className="mono text-foreground/70">{Math.round(row.ratio * 100)}% reverted</span>
        </div>
        <div className="text-[10.5px] text-foreground/55 mt-0.5">
          {row.reverted} reverted out of last {row.sample_size} · threshold {Math.round(row.threshold * 100)}%
        </div>
      </div>
    );
  }
  if (rule === "impact_spike") {
    return (
      <div className="rounded-lg border bg-rose-50/40 px-3 py-2 text-xs">
        <div className="flex items-center justify-between">
          <div className="font-medium">{row.actor_email}</div>
          <span className="mono text-foreground/70 font-semibold">{money(row.impact_total)}</span>
        </div>
        <div className="text-[10.5px] text-foreground/55 mt-0.5">
          {row.count} actions in last {row.window_h}h · threshold {money(row.threshold)}
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-lg border bg-foreground/3 px-3 py-2 text-xs">
      <div className="flex items-center justify-between">
        <div className="font-medium">{row.actor_email}{row.target_user_id ? ` → ${(row.target_user_id || "").slice(0, 8)}` : ""}</div>
        <span className="mono text-foreground/70">{row.count}× in {row.window_h}h</span>
      </div>
      {row.actions && row.actions.length > 0 && (
        <div className="text-[10.5px] text-foreground/55 mt-1 flex flex-wrap gap-1">
          {row.actions.slice(0, 6).map(a => (
            <span key={a} className="px-1.5 py-0.5 rounded bg-foreground/5 mono">{a}</span>
          ))}
        </div>
      )}
      {row.last_at && (
        <div className="text-[10px] text-foreground/45 mono mt-0.5">last: {(new Date(row.last_at)).toLocaleString()}</div>
      )}
      {row.off_hours_window && (
        <div className="text-[10px] text-foreground/55 mt-0.5">Off-hours window: {row.off_hours_window}</div>
      )}
    </div>
  );
}
